import { fetchPrivateApi } from '@/lib/api/apiServiceBase.ts';
import { PagedDto } from '@/components/table/types/PagedDto.ts';
import { PageRequest } from '@/components/table/types/PageRequest.ts';
import { TableDefinitionDto } from '@/components/table/types/TableDefinitionDto.ts';
import TourDto, {
  TourBaseDto,
} from '@/features/dashboard/tourConfig/types/TourDto.ts';
import { FilterCriteriaDto } from '@/components/table/types/FilterCriteriaDto.tsx';
import { TourStatisticsDto } from '@/features/dashboard/tours/types/TourStatisticsDto.ts';
import TourSlugDto from '@/features/dashboard/tours/types/TourSlugDto.ts';
import TourListDto from '@/features/dashboard/tours/types/TourListDto.ts';
import { buildPageRequestParams } from '@/components/table/types/TableUtil.ts';
import TourActivationReadinessDto from '@/features/dashboard/tours/types/TourActivationReadinessDto.ts';
import TourCancellationReadinessDto from '@/features/dashboard/tours/types/TourCancellationReadinessDto.ts';
import TourArchiveReadinessDto from '@/features/dashboard/tours/types/TourArchiveReadinessDto.ts';
import { CancelTourRequest } from '@/features/dashboard/tours/types/CancelTourRequest.ts';

const toursApi = {
  createTour: async (tour: TourBaseDto): Promise<TourDto> => {
    return await fetchPrivateApi<TourDto>('POST', `tours`, tour);
  },
  getTours: async (
    pageRequest: PageRequest,
    filterCriteria: FilterCriteriaDto,
  ): Promise<PagedDto<TourListDto>> => {
    const params = buildPageRequestParams(pageRequest);
    return await fetchPrivateApi<PagedDto<TourListDto>>(
      'POST',
      `tours/all?${params.toString()}`,
      filterCriteria,
    );
  },
  getFieldDefinitions: async (): Promise<TableDefinitionDto> => {
    return await fetchPrivateApi<TableDefinitionDto>(
      'GET',
      `tours/table-definition`,
    );
  },
  getTour: async (tourId: string): Promise<TourDto> => {
    return await fetchPrivateApi<TourDto>('GET', `tours/${tourId}`);
  },
  deleteTour: async (tourId: string): Promise<void> => {
    return await fetchPrivateApi<void>('DELETE', `tours/${tourId}`);
  },
  updateTour: async (tourId: string, tour: TourBaseDto): Promise<TourDto> => {
    return await fetchPrivateApi<TourDto>('PUT', `tours/${tourId}`, tour);
  },
  getActivationReadiness: async (
    tourId: string,
  ): Promise<TourActivationReadinessDto> => {
    return await fetchPrivateApi<TourActivationReadinessDto>(
      'GET',
      `tours/${tourId}/activation-readiness`,
    );
  },
  activateTour: async (tourId: string): Promise<void> => {
    return await fetchPrivateApi<void>('POST', `tours/${tourId}/activate`);
  },
  getCancellationReadiness: async (
    tourId: string,
  ): Promise<TourCancellationReadinessDto> => {
    return await fetchPrivateApi<TourCancellationReadinessDto>(
      'GET',
      `tours/${tourId}/cancellation-readiness`,
    );
  },
  cancelTour: async (
    tourId: string,
    request: CancelTourRequest,
  ): Promise<void> => {
    return await fetchPrivateApi<void>(
      'POST',
      `tours/${tourId}/cancel`,
      request,
    );
  },
  getArchiveReadiness: async (
    tourId: string,
  ): Promise<TourArchiveReadinessDto> => {
    return await fetchPrivateApi<TourArchiveReadinessDto>(
      'GET',
      `tours/${tourId}/archive-readiness`,
    );
  },
  openRegistration: async (tourId: string): Promise<void> => {
    return await fetchPrivateApi<void>(
      'POST',
      `tours/${tourId}/open-registration`,
    );
  },
  closeRegistration: async (tourId: string): Promise<void> => {
    return await fetchPrivateApi<void>(
      'POST',
      `tours/${tourId}/close-registration`,
    );
  },
  archiveTour: async (tourId: string): Promise<void> => {
    return await fetchPrivateApi<void>('POST', `tours/${tourId}/archive`);
  },
  getTourStatistics: async (tourId: string): Promise<TourStatisticsDto> => {
    return await fetchPrivateApi<TourStatisticsDto>(
      'GET',
      `tours/${tourId}/statistics`,
    );
  },
  getTourSlug: async (tourId: string): Promise<TourSlugDto> => {
    return await fetchPrivateApi<TourSlugDto>('GET', `tours/${tourId}/slug`);
  },
};

export default toursApi;
